import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { describeError } from '../services/errorMessage';
import { ladeAlleSeiten } from '../services/seitenweise';
import { fetchCustomers } from '../services/crmService';
import { fetchLeads } from '../services/leadService';
import { fetchFreelancers } from '../services/freelancerService';
import {
    MapFeature,
    customerToFeature,
    freelancerToFeature,
    leadToFeature,
    projectToFeature,
    tenderToFeature,
    toMapFeatures,
} from '../services/mapFeatures';
import {
    CrmMapMetrik,
    PlzMapping,
    RegionAggregate,
    UNBEKANNT_KEY,
    WhiteSpot,
    ZipRow,
    Zeitraum,
    aggregateByRegion,
    branchenVerteilung,
    choroplethScale,
    createClusterIndex,
    findWhiteSpots,
    letzteMonate,
    metrikWert,
} from '../services/crmMapData';

// Unreiner Teil der CRM-Deutschlandkarte (ERP/CRM-Plan Abschnitte 3.2 und 6):
// Zustand der Ebenen, Zeitraum, Metrik und Drilldown — plus das Laden der
// Daten (Aggregat-RPCs, Leads, Ausschreibungen, Kunden, Projekte) und der
// Geometrien.
//
// Die Ableitungen selbst (Aggregation, Conversion, weiße Flecken, Klassen,
// Cluster) stehen pur und getestet in src/services/crmMapData.ts und werden
// hier nur AUFGERUFEN, nicht nachgebaut. Die Komponente bleibt ein duenner
// Container (Standing Decision).

// ─── Geometrien: lazy, gecacht, nie im initialen Bundle ─────────────────────
//
// public/geodata/README.md, Abschnitt „WICHTIG — Lazy Loading": diese Dateien
// duerfen NICHT statisch importiert werden. Ein `import x from '.../*.json'`
// backte 1,2 MB Geometrie in jedes Bundle — auch fuer jeden Nutzer, der die
// Karte nie oeffnet (Befund 5.5 im Plan). Deshalb `fetch` zur Laufzeit.
//
// Der Cache liegt im MODUL-Scope und nicht im Hook: er ueberlebt damit das
// Aus- und Wiedereinblenden der Ansicht. Gespeichert wird das PROMISE, nicht
// das Ergebnis — zwei gleichzeitige Aufrufe (Karte oeffnet, Drilldown klickt)
// teilen sich dann eine Anfrage statt zwei zu starten.

export interface GeoFeature {
    type: 'Feature';
    properties: Record<string, any>;
    geometry: any;
}

export interface GeoCollection {
    type: 'FeatureCollection';
    features: GeoFeature[];
}

let bundeslaenderCache: Promise<GeoCollection> | null = null;
let landkreiseCache: Promise<GeoCollection> | null = null;
let plzCache: Promise<PlzMapping> | null = null;

const ladeJson = async <T,>(pfad: string): Promise<T> => {
    const res = await fetch(pfad);
    if (!res.ok) throw new Error(`${pfad} konnte nicht geladen werden (HTTP ${res.status}).`);
    return (await res.json()) as T;
};

/**
 * AGS -> Klartextname, gefuellt aus jeder geladenen Geometrieebene.
 *
 * Bewusst im Modul-Scope und nicht im Komponentenzustand: die Zuordnung waechst
 * nur (Bundeslaender beim Oeffnen, Kreise beim ersten Drilldown) und wird nie
 * ungueltig. Wer einmal auf Kreisebene war, sieht die Kreisnamen danach auch in
 * der Weiße-Flecken-Liste auf Bundesebene — ohne die 739 KB erneut zu holen und
 * ohne dass ein Effekt Zustand nachpflegen muesste.
 */
const REGION_NAMEN = new Map<string, string>();

const merkeNamen = (geo: GeoCollection): GeoCollection => {
    for (const f of geo.features || []) {
        const ags = String(f.properties?.AGS ?? '');
        const name = String(f.properties?.name ?? '');
        if (ags && name) REGION_NAMEN.set(ags, name);
    }
    return geo;
};

/** Name zu einem amtlichen Schluessel, oder null solange die Ebene nicht geladen ist. */
export const regionName = (ags: string): string | null => REGION_NAMEN.get(ags) ?? null;

/** Bundesland-Geometrien (16 Features, ~59 KB). */
export const ladeBundeslaender = (): Promise<GeoCollection> => {
    // Fehlschlaege werden NICHT gecacht: sonst bliebe ein einmaliger
    // Netzaussetzer bis zum Neuladen der Seite bestehen.
    if (!bundeslaenderCache) {
        bundeslaenderCache = ladeJson<GeoCollection>('/geodata/bundeslaender.geo.json')
            .then(merkeNamen)
            .catch((e) => {
                bundeslaenderCache = null;
                throw e;
            });
    }
    return bundeslaenderCache;
};

/** Kreis-Geometrien (401 Features, ~739 KB) — erst beim Drilldown. */
export const ladeLandkreise = (): Promise<GeoCollection> => {
    if (!landkreiseCache) {
        landkreiseCache = ladeJson<GeoCollection>('/geodata/landkreise.geo.json')
            .then(merkeNamen)
            .catch((e) => {
                landkreiseCache = null;
                throw e;
            });
    }
    return landkreiseCache;
};

/** PLZ -> {bundesland, ags_kreis} (8.168 Eintraege, ~383 KB). */
export const ladePlzMapping = (): Promise<PlzMapping> => {
    if (!plzCache) {
        plzCache = ladeJson<PlzMapping>('/geodata/plz_zuordnung.json').catch((e) => {
            plzCache = null;
            throw e;
        });
    }
    return plzCache;
};

// ─── Ebenen ─────────────────────────────────────────────────────────────────

/** Die sechs Ebenen aus Plan Abschnitt 6, Reihenfolge = Reihenfolge im Filter. */
export const CRM_MAP_LAYERS = [
    'kunden',
    'leads',
    'radar',
    'projekte',
    'freelancer',
    'choropleth',
] as const;

export type CrmMapLayer = (typeof CRM_MAP_LAYERS)[number];

export interface LayerBeschreibung {
    key: CrmMapLayer;
    label: string;
    /** Kurztext im Filter — nennt die Quelle, nicht die Darstellung. */
    hinweis: string;
    /** true = Schalter sichtbar, aber nicht bedienbar (Daten folgen). */
    baldVerfuegbar?: boolean;
}

export const LAYER_INFO: Record<CrmMapLayer, LayerBeschreibung> = {
    kunden: { key: 'kunden', label: 'Gewonnene Kunden', hinweis: 'Kundenstamm mit Koordinaten' },
    leads: { key: 'leads', label: 'Leads nach Status', hinweis: 'Pipeline; Scraper-Funde ≤ 7 Tage pulsieren' },
    radar: { key: 'radar', label: 'Ausschreibungs-Radar', hinweis: 'Ausschreibungen mit Abgabefrist' },
    projekte: { key: 'projekte', label: 'Aktive Projekte', hinweis: 'Wo GEFAS heute präsent ist' },
    freelancer: {
        key: 'freelancer',
        // Seit Phase D1 scharf: die Tabelle `freelancers` existiert, und die
        // Ebene laedt die aktiven Stammsaetze. Der Hinweis nennt ausdruecklich
        // die Unschaerfe der Position — sie ist eine Zusage an den
        // Freelancer, keine Ungenauigkeit (mapFeatures.freelancerToFeature).
        label: 'Freelancer',
        hinweis: 'Aktive Freelancer; Position bewusst grob (~1 km) + Einsatzradius',
    },
    choropleth: { key: 'choropleth', label: 'Flächen-Einfärbung', hinweis: 'Server-Aggregate je Region' },
};

const STANDARD_LAYER: Record<CrmMapLayer, boolean> = {
    kunden: true,
    leads: true,
    radar: true,
    projekte: true,
    // Standardmaessig AUS: die Ebene beantwortet eine Ressourcenfrage („wen
    // koennte ich schicken?"), nicht die Vertriebsfrage, mit der diese Karte
    // geoeffnet wird. Wer sie braucht, schaltet sie zu — umgekehrt haetten
    // alle anderen dauerhaft Marker auf der Karte, die sie nichts angehen.
    freelancer: false,
    choropleth: true,
};

// ─── Drilldown ──────────────────────────────────────────────────────────────

export interface DrilldownState {
    /** 'bund' = ganz Deutschland, 'land' = ein Bundesland, 'kreis' = ein Kreis. */
    ebene: 'bund' | 'land' | 'kreis';
    /** 2-stelliger AGS des gewaehlten Bundeslands. */
    landAgs?: string;
    /** 5-stelliger AGS des gewaehlten Kreises. */
    kreisAgs?: string;
}

// ─── Rohdaten ───────────────────────────────────────────────────────────────

interface RevenueRow {
    zip: string | null;
    netto_summe: number | string | null;
    rechnungen: number | null;
}

interface CountsRow {
    zip: string | null;
    kunden: number | null;
    leads_offen: number | null;
    leads_gewonnen: number | null;
    leads_verloren: number | null;
    ausschreibungen: number | null;
}

export interface CrmMapDaten {
    features: MapFeature[];
    zipRows: ZipRow[];
    /** Gesetzt, wenn die Aggregat-RPCs fehlen (Migration 17 noch nicht gefahren). */
    aggregateFehlen: boolean;
}

const LEER: CrmMapDaten = { features: [], zipRows: [], aggregateFehlen: false };

/**
 * Projekte fuer die Karte. Bewusst schlank selektiert — nicht die volle
 * `data`-Spalte (die traegt Berichte, Checklisten und ggf. Fotos und waere
 * fuer einen Marker masslos), sondern nur `data->metadata` per PostgREST-
 * Projektion (gleiches Muster wie cloudService.loadProjects und
 * erpSteeringService). `projectToFeature` liest genau dieses schmale Objekt
 * ueber `p.metadata` (siehe mapFeatures.ProjectFeatureLike).
 *
 * Seitenweise: projects waechst auf die stille 1000er-Grenze von PostgREST
 * zu (Vorfall 23.10.2026) — dahinter fehlten Marker ohne jeden Hinweis.
 */
const ladeProjektFeatures = async (): Promise<MapFeature[]> => {
    const data = await ladeAlleSeiten((von, bis) =>
        supabase
            .from('projects')
            .select('id, name, status, project_number, user_id, metadata:data->metadata')
            .neq('status', 'archived')
            .order('id')
            .range(von, bis))
        .catch((error: unknown) => { throw new Error(describeError(error)); });
    return toMapFeatures(data as any[], projectToFeature);
};

const ladeAusschreibungsFeatures = async (): Promise<MapFeature[]> => {
    const { data, error } = await supabase
        .from('tender_opportunities')
        .select('id, titel, auftraggeber, lat, lng, status, zip, bundesland, leistungsort, abgabefrist, dokumente_url, cpv_codes')
        .not('status', 'in', '("ignoriert","verloren")');
    if (error) throw new Error(describeError(error));
    return toMapFeatures(data as any[], (row: any) =>
        tenderToFeature({
            id: row.id,
            titel: row.titel,
            auftraggeber: row.auftraggeber,
            lat: row.lat,
            lng: row.lng,
            status: row.status,
            zip: row.zip,
            bundesland: row.bundesland,
            leistungsort: row.leistungsort,
            abgabefrist: row.abgabefrist,
            dokumenteUrl: row.dokumente_url,
            cpvCodes: row.cpv_codes,
        }),
    );
};

/**
 * Freelancer-Ebene (Plan Abschnitt 6, Phase D1). NUR aktive Stammsaetze:
 * pausiert oder gesperrt heisst „nicht beauftragen", und ein Marker, den man
 * nicht anfragen darf, ist auf einer Ressourcenkarte ein Fehler.
 *
 * Die Position ist bereits im Adapter gerundet (freelancerToFeature) — hier
 * kommen die exakten Koordinaten gar nicht erst an der Karte an.
 */
const ladeFreelancerFeatures = async (): Promise<MapFeature[]> => {
    const rows = await fetchFreelancers({ status: ['aktiv'] });
    return toMapFeatures(rows, freelancerToFeature);
};

// ─── Hook ───────────────────────────────────────────────────────────────────

export interface UseCrmMapResult {
    laedt: boolean;
    fehler: string | null;
    /** Die Aggregat-RPCs fehlen — das Choropleth bleibt leer, die Marker nicht. */
    aggregateFehlen: boolean;
    neuLaden: () => void;

    layer: Record<CrmMapLayer, boolean>;
    toggleLayer: (key: CrmMapLayer) => void;

    zeitraum: Zeitraum;
    monate: number;
    setMonate: (n: number) => void;

    metrik: CrmMapMetrik;
    setMetrik: (m: CrmMapMetrik) => void;

    drilldown: DrilldownState;
    zeigeBund: () => void;
    zeigeLand: (landAgs: string) => void;
    zeigeKreis: (kreisAgs: string) => void;

    /** Branchenfilter aus dem Donut. null = alle. '' = „ohne Branche". */
    brancheFilter: string | null;
    setBrancheFilter: (b: string | null) => void;

    /** Alle geladenen Features, ungefiltert — Grundlage der Statistik. */
    alleFeatures: MapFeature[];
    /** Was nach Ebenen-, Branchen- und Drilldown-Filter uebrig bleibt. */
    sichtbareFeatures: MapFeature[];
    /** Supercluster-Index; NUR bei Datenaenderung neu gebaut. */
    clusterIndex: ReturnType<typeof createClusterIndex>;

    /** Regionen auf der aktuellen Drilldown-Ebene. */
    regionen: RegionAggregate[];
    regionNachKey: Map<string, RegionAggregate>;
    skala: ReturnType<typeof choroplethScale>;
    weisseFlecken: WhiteSpot[];
    branchen: ReturnType<typeof branchenVerteilung>;

    /** Geometrien der aktuellen Ebene; null, solange sie laden. */
    geometrien: GeoCollection | null;
    geometrienLaden: boolean;
    plzMapping: PlzMapping | null;
}

/**
 * Auf welcher Ebene wird aggregiert? Bund und „ein Bundesland" zeigen beide
 * Kreise, sobald hineingezoomt wurde — die Bundeslandkarte ist die
 * Uebersicht, alles darunter arbeitet auf Kreisen.
 */
const levelFuer = (d: DrilldownState) => (d.ebene === 'bund' ? 'bundesland' : 'kreis');

export const useCrmMap = (): UseCrmMapResult => {
    const [layer, setLayer] = useState<Record<CrmMapLayer, boolean>>({ ...STANDARD_LAYER });
    const [monate, setMonate] = useState(12);
    const [metrik, setMetrik] = useState<CrmMapMetrik>('umsatz');
    const [drilldown, setDrilldown] = useState<DrilldownState>({ ebene: 'bund' });
    const [brancheFilter, setBrancheFilter] = useState<string | null>(null);

    const [daten, setDaten] = useState<CrmMapDaten>(LEER);
    const [laedt, setLaedt] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);
    const [nachladen, setNachladen] = useState(0);

    const [plzMapping, setPlzMapping] = useState<PlzMapping | null>(null);
    const [geometrien, setGeometrien] = useState<GeoCollection | null>(null);
    const [geometrienLaden, setGeometrienLaden] = useState(true);

    // Laufende Nummer je Ladevorgang: eine spaet eintreffende Antwort zum alten
    // Zeitraum darf das Ergebnis des neuen nicht ueberschreiben (Muster
    // useNearbyProjects.generationRef).
    const generationRef = useRef(0);

    const zeitraum = useMemo(() => letzteMonate(monate), [monate]);

    // ── Geometrien ──────────────────────────────────────────────────────────
    // Die Kreisdatei (739 KB) wird ERST beim Drilldown geholt. Wer nur die
    // Bundeslandkarte ansieht, laedt sie nie.
    const brauchtKreise = drilldown.ebene !== 'bund';

    useEffect(() => {
        let abgebrochen = false;
        setGeometrienLaden(true);
        const laden = brauchtKreise ? ladeLandkreise() : ladeBundeslaender();
        laden
            .then((geo) => {
                if (!abgebrochen) setGeometrien(geo);
            })
            .catch((e) => {
                if (abgebrochen) return;
                // Ohne Geometrien bleibt die Karte mit ihren Markern benutzbar;
                // nur die Flaechen fehlen. Deshalb Hinweis statt Abbruch.
                setGeometrien(null);
                setFehler(describeError(e));
            })
            .finally(() => {
                if (!abgebrochen) setGeometrienLaden(false);
            });
        return () => {
            abgebrochen = true;
        };
    }, [brauchtKreise]);

    useEffect(() => {
        let abgebrochen = false;
        ladePlzMapping()
            .then((m) => {
                if (!abgebrochen) setPlzMapping(m);
            })
            .catch((e) => {
                if (!abgebrochen) setFehler(describeError(e));
            });
        return () => {
            abgebrochen = true;
        };
    }, []);

    // ── Daten ───────────────────────────────────────────────────────────────
    useEffect(() => {
        const generation = (generationRef.current += 1);
        let abgebrochen = false;

        const laden = async () => {
            setLaedt(true);
            setFehler(null);
            // Nur der Kundenstamm ist eine Kernquelle: sein Fehler (Netz,
            // Auth, RLS) muss sichtbar werden, sonst wirkt die Karte
            // faelschlich leer statt gestoert. Die uebrigen Quellen haengen
            // an noch nicht ausgerollten Migrationen (Leads, Ausschreibungen,
            // Aggregate) oder sind eine reine Zusatzebene (Freelancer) und
            // duerfen weiterhin still leer bleiben.
            let kundenFehler: unknown = null;
            try {
                // Alle Quellen parallel. Nacheinander waere die Karte
                // sechsmal so lange leer — und jede einzelne Quelle darf
                // ausfallen, ohne die uebrigen mitzureissen: eine fehlende
                // Migration (Leads, Ausschreibungen, Aggregate) soll die
                // Kundenkarte nicht abschalten.
                const [kunden, leads, tender, projekte, freelancer, umsatz, counts] = await Promise.all([
                    fetchCustomers().catch((e) => {
                        kundenFehler = e;
                        return [];
                    }),
                    fetchLeads().catch(() => []),
                    ladeAusschreibungsFeatures().catch(() => [] as MapFeature[]),
                    ladeProjektFeatures().catch(() => [] as MapFeature[]),
                    ladeFreelancerFeatures().catch(() => [] as MapFeature[]),
                    supabase
                        .rpc('crm_map_revenue_by_zip', { p_from: zeitraum.von, p_to: zeitraum.bis })
                        .then((r) => (r.error ? null : ((r.data || []) as RevenueRow[])))
                        .catch(() => null),
                    supabase
                        .rpc('crm_map_counts_by_zip')
                        .then((r) => (r.error ? null : ((r.data || []) as CountsRow[])))
                        .catch(() => null),
                ]);

                if (abgebrochen || generation !== generationRef.current) return;

                const features: MapFeature[] = [
                    ...toMapFeatures(kunden as any[], customerToFeature),
                    ...toMapFeatures(leads as any[], leadToFeature),
                    ...tender,
                    ...projekte,
                    ...freelancer,
                ];

                const zipRows: ZipRow[] = [
                    ...(umsatz || []).map((r) => ({
                        zip: r.zip,
                        nettoSumme: Number(r.netto_summe ?? 0),
                        rechnungen: Number(r.rechnungen ?? 0),
                    })),
                    ...(counts || []).map((r) => ({
                        zip: r.zip,
                        kunden: Number(r.kunden ?? 0),
                        leadsOffen: Number(r.leads_offen ?? 0),
                        leadsGewonnen: Number(r.leads_gewonnen ?? 0),
                        leadsVerloren: Number(r.leads_verloren ?? 0),
                        ausschreibungen: Number(r.ausschreibungen ?? 0),
                    })),
                    // Projekte je PLZ kommen NICHT aus einer RPC: die
                    // Projektadresse ist Freitext, und derselbe Regex liefe
                    // sonst zweimal (einmal in SQL, einmal hier). Die
                    // Weiße-Flecken-Analyse braucht die Zahl, also wird sie
                    // aus den ohnehin geladenen Markern gebildet.
                    ...projekte.map((p) => ({ zip: plzAusAdresse(p), projekte: 1 })),
                ];

                setDaten({
                    features,
                    zipRows,
                    // Beide RPCs still gescheitert = Migration 17 fehlt.
                    aggregateFehlen: umsatz === null && counts === null,
                });

                // Erst NACH setDaten setzen: die Karte soll mit den uebrigen
                // Ebenen rendern, aber den Kundenstamm-Ausfall nicht
                // verschweigen (sonst wirkt sie faelschlich leer).
                if (kundenFehler) {
                    setFehler(`Kundenstamm konnte nicht geladen werden: ${describeError(kundenFehler)}`);
                }
            } catch (e) {
                if (!abgebrochen && generation === generationRef.current) setFehler(describeError(e));
            } finally {
                if (!abgebrochen && generation === generationRef.current) setLaedt(false);
            }
        };

        void laden();
        return () => {
            abgebrochen = true;
        };
    }, [zeitraum.von, zeitraum.bis, nachladen]);

    // ── Filter ──────────────────────────────────────────────────────────────

    const sichtbareFeatures = useMemo(() => {
        const erlaubt = (f: MapFeature): boolean => {
            if (f.kind === 'customer') return layer.kunden;
            if (f.kind === 'lead') return layer.leads;
            if (f.kind === 'tender') return layer.radar;
            if (f.kind === 'project') return layer.projekte;
            if (f.kind === 'freelancer') return layer.freelancer;
            return true;
        };
        return daten.features.filter((f) => {
            if (!erlaubt(f)) return false;
            if (brancheFilter === null) return true;
            // Der Branchenfilter greift NUR dort, wo es eine Branche gibt.
            // Projekte und Ausschreibungen tragen keine — sie wuerden sonst bei
            // jedem Donut-Klick verschwinden, obwohl der Klick nichts ueber sie
            // aussagt.
            if (f.kind !== 'customer' && f.kind !== 'lead') return true;
            return String(f.meta?.branche ?? '') === brancheFilter;
        });
    }, [daten.features, layer, brancheFilter]);

    // Der Index haengt AUSSCHLIESSLICH an den sichtbaren Features — nicht am
    // Ausschnitt und nicht am Zoom. Genau das ist der Punkt: pro Kartenbewegung
    // wird nur noch abgefragt, nicht neu indiziert (Befund 5.2).
    const clusterIndex = useMemo(() => createClusterIndex(sichtbareFeatures), [sichtbareFeatures]);

    // ── Regionen ────────────────────────────────────────────────────────────

    const level = levelFuer(drilldown);

    const regionen = useMemo(() => {
        const alle = aggregateByRegion(daten.zipRows, plzMapping, level);
        if (drilldown.ebene === 'land' && drilldown.landAgs) {
            // Auf Kreisebene innerhalb eines Bundeslandes: nur dessen Kreise.
            // Der 2-stellige AGS ist das Praefix des 5-stelligen.
            return alle.filter((r) => r.key.startsWith(drilldown.landAgs!) || r.key === UNBEKANNT_KEY);
        }
        if (drilldown.ebene === 'kreis' && drilldown.kreisAgs) {
            return alle.filter((r) => r.key === drilldown.kreisAgs);
        }
        return alle;
    }, [daten.zipRows, plzMapping, level, drilldown]);

    const regionNachKey = useMemo(() => {
        const m = new Map<string, RegionAggregate>();
        for (const r of regionen) m.set(r.key, r);
        return m;
    }, [regionen]);

    const skala = useMemo(
        () =>
            choroplethScale(
                regionen.filter((r) => !r.unbekannt).map((r) => metrikWert(r, metrik)),
                { formatWert: (n) => formatMetrik(n, metrik) },
            ),
        [regionen, metrik],
    );

    // Weiße Flecken IMMER auf Kreisebene: „Bayern hat keinen Kunden" ist keine
    // Erkenntnis, „Landkreis Konstanz hat keinen" ist eine Adresse.
    const weisseFlecken = useMemo(
        () => findWhiteSpots(aggregateByRegion(daten.zipRows, plzMapping, 'kreis')),
        [daten.zipRows, plzMapping],
    );

    const branchen = useMemo(
        () => branchenVerteilung(daten.features.filter((f) => f.kind === 'customer' || f.kind === 'lead')),
        [daten.features],
    );

    // ── Aktionen ────────────────────────────────────────────────────────────

    const toggleLayer = useCallback((key: CrmMapLayer) => {
        if (LAYER_INFO[key].baldVerfuegbar) return;
        setLayer((v) => ({ ...v, [key]: !v[key] }));
    }, []);

    const zeigeBund = useCallback(() => setDrilldown({ ebene: 'bund' }), []);
    const zeigeLand = useCallback((landAgs: string) => setDrilldown({ ebene: 'land', landAgs }), []);
    const zeigeKreis = useCallback(
        (kreisAgs: string) =>
            setDrilldown((d) => ({ ebene: 'kreis', landAgs: d.landAgs ?? kreisAgs.slice(0, 2), kreisAgs })),
        [],
    );

    const neuLaden = useCallback(() => setNachladen((n) => n + 1), []);

    return {
        laedt,
        fehler,
        aggregateFehlen: daten.aggregateFehlen,
        neuLaden,
        layer,
        toggleLayer,
        zeitraum,
        monate,
        setMonate,
        metrik,
        setMetrik,
        drilldown,
        zeigeBund,
        zeigeLand,
        zeigeKreis,
        brancheFilter,
        setBrancheFilter,
        alleFeatures: daten.features,
        sichtbareFeatures,
        clusterIndex,
        regionen,
        regionNachKey,
        skala,
        weisseFlecken,
        branchen,
        geometrien,
        geometrienLaden,
        plzMapping,
    };
};

// ─── Formatierung ───────────────────────────────────────────────────────────

const eur0 = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
});

/**
 * Ein Metrikwert als Text. Umsatz kommt in CENT aus der Datenbank und wird
 * hier — und nur hier — in Euro umgerechnet; alle uebrigen Metriken sind
 * reine Anzahlen bzw. Prozentpunkte.
 */
export const formatMetrik = (n: number, metrik: CrmMapMetrik): string => {
    if (!Number.isFinite(n)) return '—';
    if (metrik === 'umsatz') return eur0.format(n / 100);
    if (metrik === 'conversion') return `${Math.round(n)} %`;
    return String(Math.round(n));
};

/**
 * PLZ aus einem Projekt-Feature. Die Projektadresse ist Freitext
 * (`metadata.projectAddress`); dieselbe Heuristik wie `crm_map_zip()` in
 * supabase/crm_map_aggregates.sql — erste alleinstehende 5-stellige Zahl.
 */
const plzAusAdresse = (f: MapFeature): string | null => {
    const adresse = String(f?.meta?.address ?? '');
    const treffer = adresse.match(/(?:^|\D)(\d{5})(?!\d)/);
    return treffer ? treffer[1] : null;
};
